import * as React from 'react';

export enum SvgIconName {
  GOOD_STATUS = 'GOOD_STATUS',
  BAD_STATUS = 'BAD_STATUS',
  POSITIVE_DELTA = 'POSITIVE_DELTA',
  NEGATIVE_DELTA = 'NEGATIVE_DELTA',
  SUSPENDED = 'SUSPENDED',
  CAMERA = 'CAMERA',
}

export interface SvgIconProps {
  readonly name: SvgIconName;
  readonly altText: string;
  readonly color?: string;
  readonly style?: React.CSSProperties;
  readonly testId?: string;
  readonly size?: number;
}

export const SvgIcon = (props: SvgIconProps): JSX.Element | null => {
  const iconSrc = getIconSrc(props.name, props.color, props.size);
  if (iconSrc === '') {
    return null;
  }

  return <img src={iconSrc} style={props.style} alt={props.altText} data-tb-test-id={props.testId} />;
};

export const getIconSrc = (name: SvgIconName, color?: string, size?: number): string => {
  switch (name) {
    case SvgIconName.GOOD_STATUS:
      return encodedIconWithColorOrEmpty(size && size === 20 ? GoodStatus20 : GoodStatus14, color);
    case SvgIconName.BAD_STATUS:
      return encodedIconWithColorOrEmpty(size && size === 20 ? BadStatus20 : BadStatus14, color);
    case SvgIconName.POSITIVE_DELTA:
      return encodedIconWithColorOrEmpty(size && size === 20 ? PositiveDelta20 : PositiveDelta14, color);
    case SvgIconName.NEGATIVE_DELTA:
      return encodedIconWithColorOrEmpty(size && size === 20 ? NegativeDelta20 : NegativeDelta14, color);
    case SvgIconName.SUSPENDED:
      return encodeIconString(ServerAlertListSuspended);
    case SvgIconName.CAMERA:
      return encodedIconWithColorOrEmpty(Camera14, color);
    default:
      return '';
  }
};

const encodeIconString = (iconSrc: string): string => {
  return `data:image/svg+xml,${encodeURIComponent(iconSrc)}`;
};

const encodedIconWithColorOrEmpty = (icon: (color: string) => string, color?: string): string => {
  if (color === undefined) {
    return '';
  }

  return encodeIconString(icon(color));
};

/* tslint:disable:max-line-length */

// From clear_14px-small.svg
const BadStatus14 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
    <polygon fill="${color}" fill-rule="evenodd" points="12.314 1 13.021 1.707 7.717 7.01 13.021 12.314 12.314 13.021 7.01 7.717 1.707 13.021 1 12.314 6.303 7.01 1 1.707 1.707 1 7.01 6.303"></polygon>
  </svg>`;

// From clear_18px-large.svg
const BadStatus20 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 19 19">
    <polygon fill="${color}" fill-rule="evenodd" points="16.296 1.001 17.003 1.708 9.708 9.002 17.003 16.297 16.296 17.004 9 9.709 1.706 17.003 .999 16.296 8.293 9.002 1 1.707 1.707 1 9.001 8.295"></polygon>
  </svg>`;

// From camera_base_14px.svg
const Camera14 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M10 8C10 9.65685 8.65685 11 7 11C5.34315 11 4 9.65685 4 8C4 6.34315 5.34315 5 7 5C8.65685 5 10 6.34315 10 8ZM9 8C9 9.10457 8.10457 10 7 10C5.89543 10 5 9.10457 5 8C5 6.89543 5.89543 6 7 6C8.10457 6 9 6.89543 9 8Z" fill="${color}"></path>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.61803 2C5.23926 2 4.893 2.214 4.72361 2.55279L4 4H2C1.44772 4 1 4.44772 1 5V12C1 12.5523 1.44772 13 2 13H12C12.5523 13 13 12.5523 13 12V5C13 4.44772 12.5523 4 12 4H10L9.27639 2.55279C9.107 2.214 8.76074 2 8.38197 2H5.61803ZM4.61803 5L5.61803 3L8.38197 3L9.38197 5H12V12H2V5H4.61803Z" fill="${color}"></path>
  </svg>`;

// From check_14px-small.svg
const GoodStatus14 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
    <path fill="${color}" fill-rule="evenodd" d="M12.0713218,1 L12.865698,1.4359326 L7.33039459,12.2578763 C7.2968669,12.3177578 7.25156942,12.3670935 7.19894324,12.4046227 L7.12638767,12.4471038 L7.10956871,12.4542337 C6.9448756,12.5309428 6.74233651,12.50338 6.60334716,12.3704999 L2,8.12660356 L2.63092578,7.4797854 L6.81515537,11.3228296 L12.0713218,1 Z"></path>
  </svg>`;

// From check_18px-large.svg
const GoodStatus20 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 19 19">
    <path fill="${color}" fill-rule="evenodd" d="M17.0060568,2.21572326 L8.94085833,16.9250941 C8.90733064,16.9849756 8.86203316,17.0343113 8.80940698,17.0718405 L8.73685141,17.1143217 L8.72003245,17.1214516 C8.55533934,17.1981607 8.35280025,17.1705979 8.2138109,17.0377178 L1,10.2774607 L1.63092578,9.63064254 L8.42561911,15.9900474 L16.2060591,1.79024295 L17.0060568,2.21572326 Z"></path>
  </svg>`;

// From metric_up_solid_14px-small.svg
const PositiveDelta14 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
    <path d="M6.45567 1.31711C6.68783 0.894297 7.31217 0.894297 7.54433 1.31711L12.9267 11.1198C13.1456 11.5185 12.8479 12 12.3823 12H1.61765C1.1521 12 0.85437 11.5185 1.07333 11.1198L6.45567 1.31711Z" fill="${color}"></path>
  </svg>`;

// From metric_up_solid_20px-xlarge.svg 1 2 18 16
const PositiveDelta20 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
    <path fill="${color}" fill-rule="evenodd" d="M10.494343,2.13590486 C10.6508422,2.22489863 10.7804139,2.35449088 10.8693828,2.51100423 L18.826973,16.5099235 C19.0999017,16.9900571 18.9319289,17.6005348 18.4517952,17.8734636 C18.3011674,17.9590869 18.1308771,18.0041046 17.957614,18.0041046 L2.03949483,18.0041046 C1.48721008,18.0041046 1.03949483,17.5563894 1.03949483,17.0041046 C1.03949483,16.8307878 1.08454047,16.6604465 1.17021429,16.5097855 L9.13074336,2.51086631 C9.40374825,2.03077593 10.0142526,1.86289997 10.494343,2.13590486 Z"></path>
  </svg>`;

// From metric_down_solid_14px-small.svg
const NegativeDelta14 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7.54433 11.6829C7.31217 12.1057 6.68783 12.1057 6.45567 11.6829L1.07333 1.88025C0.854369 1.48147 1.1521 1 1.61765 1H12.3823C12.8479 1 13.1456 1.48147 12.9267 1.88025L7.54433 11.6829Z" fill="${color}"></path>
  </svg>`;

// From metric_down_solid_20px-xlarge.svg
const NegativeDelta20 = (color: string): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
    <path fill="${color}" fill-rule="evenodd" d="M10.494343,17.8912938 C10.6508422,17.8023 10.7804139,17.6727077 10.8693828,17.5161944 L18.826973,3.51727516 C19.0999017,3.03714147 18.9319289,2.42666377 18.4517952,2.15373505 C18.3011674,2.06811171 18.1308771,2.023094 17.957614,2.023094 L2.03949483,2.023094 C1.48721008,2.023094 1.03949483,2.47080925 1.03949483,3.023094 C1.03949483,3.19641083 1.08454047,3.36675213 1.17021429,3.51741308 L9.13074336,17.5163323 C9.40374825,17.9964227 10.0142526,18.1642986 10.494343,17.8912938 Z"></path>
  </svg>`;

// From Server_Alert_List_Suspended.svg
const ServerAlertListSuspended = `<?xml version="1.0" encoding="UTF-8"?>
  <!-- Generator: Adobe Illustrator 23.0.3, SVG Export Plug-In . SVG Version: 6.00 Build 0)  -->
  <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" id="Layer_1" x="0px" y="0px" width="19px" height="19px" viewBox="0 0 19 19" style="enable-background:new 0 0 19 19;" xml:space="preserve">
  <style type="text/css">
  .st0{fill-rule:evenodd;clip-rule:evenodd;fill:#666666;}
  .st1{fill:#666666;}
  </style>
  <rect id="Pause2_1_" x="16" y="11" class="st0" width="2" height="7"></rect>
  <rect id="Pause1_1_" x="13" y="11" class="st0" width="2" height="7"></rect>
  <rect id="Square" x="8" y="13" class="st0" width="2" height="2"></rect>
  <rect id="Square_1_" x="8" y="13" class="st0" width="2" height="2"></rect>
  <rect id="Line_1_" x="8" y="6" class="st0" width="2" height="6"></rect>
  <path class="st1" d="M12,16H1.8L9,3l3.9,7H14L9.9,2.5C9.7,2.2,9.4,2,9,2S8.3,2.2,8.1,2.5L1,15.5c-0.2,0.3-0.2,0.7,0,1  C1.1,16.8,1.5,17,1.8,17H12V16z"></path>
  </svg>`;
